import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import api, { API_BASE } from "@/lib/api";
import {
  buildWsUrl, defaultViewport, diffSentinel, formatCountdown, keyMessage, normalizePoint, SENTINEL,
} from "@/lib/dpdManualLogin";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, CheckCircle2, Loader2, Lock, Monitor, Plug, RotateCcw, Smartphone, X } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);
const PHASE_LABEL = {
  idle: "Not connected", connecting: "Connecting…", live: "Live", success: "Connected to DPD",
  error: "Disconnected", ended: "Session ended",
};
const PHASE_DOT = {
  idle: "bg-slate-300", connecting: "bg-amber-400 animate-pulse", live: "bg-emerald-500",
  success: "bg-emerald-600", error: "bg-red-500", ended: "bg-slate-400",
};
const END_REASONS = {
  ttl: "The 10-minute limit was reached.", idle: "Closed after 2 minutes without input.",
  user_cancelled: "Cancelled.", disconnected: "The connection dropped.",
};

const ConnectionIndicator = ({ phase, remaining }) => (
  <div className="flex items-center gap-3 text-xs font-mono" data-testid="manual-login-indicator">
    <span className="flex items-center gap-1.5">
      <span className={`w-2.5 h-2.5 rounded-full ${PHASE_DOT[phase]}`} />
      <span data-testid="manual-login-phase">{PHASE_LABEL[phase]}</span>
    </span>
    {remaining != null && (
      <span className="px-2 py-0.5 rounded bg-brand-surface border border-brand-border"
            data-testid="manual-login-countdown">{formatCountdown(remaining)}</span>
    )}
  </div>
);

const SecurityNotice = () => (
  <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 md:p-3 text-[11px] md:text-xs leading-snug text-emerald-900"
       data-testid="manual-login-security-note">
    <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <div>
      This is the real DPD portal, running in a secure browser on our server. What you type is sent only to DPD
      through an encrypted same-origin connection. My Choice never saves or logs your DPD username, password,
      codes or screen images — only the resulting login session is kept, encrypted, so imports can run.
    </div>
  </div>
);

const ViewportPicker = ({ value, onChange, disabled }) => (
  <div className="inline-flex rounded-lg border border-brand-border p-0.5" data-testid="manual-login-viewport">
    {[["desktop", Monitor, "Desktop"], ["mobile", Smartphone, "Mobile"]].map(([v, Icon, label]) => (
      <button key={v} type="button" disabled={disabled} onClick={() => onChange(v)}
              data-testid={`manual-login-viewport-${v}`}
              className={`px-2.5 py-1 text-xs rounded-md flex items-center gap-1 transition-colors ${value === v ? "bg-brand-navy text-white" : "text-brand-text-muted hover:bg-brand-surface"}`}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </button>
    ))}
  </div>
);

function useManualLoginSession(accountId, onSuccess, onExisting) {
  const wsRef = useRef(null);
  const connectingRef = useRef(false);
  const sessionRef = useRef(null);
  const [phase, setPhaseState] = useState("idle");
  const phaseRef = useRef("idle");
  const setPhase = useCallback((p) => { phaseRef.current = p; setPhaseState(p); }, []);
  const [message, setMessage] = useState("");
  const [frameUrl, setFrameUrl] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [frameSize, setFrameSize] = useState({ width: 1280, height: 800 });

  const send = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const connect = useCallback(async (viewport) => {
    if (connectingRef.current || wsRef.current) return;
    connectingRef.current = true;
    setPhase("connecting"); setMessage(""); onExisting(null);
    let s;
    try {
      ({ data: s } = await api.post(`/integrations/dpd/accounts/${accountId}/manual-login/start`));
    } catch (e) {
      connectingRef.current = false;
      setPhase("error"); setMessage(e?.response?.data?.detail || "Could not open a DPD session. Retry.");
      if (e?.response?.status === 409) onExisting(true);
      return;
    }
    connectingRef.current = false;
    sessionRef.current = s;
    setExpiresAt(s.expires_at);
    const ws = new WebSocket(buildWsUrl(API_BASE, s.ws_path, s.session_id, viewport));
    ws.binaryType = "blob";
    wsRef.current = ws;
    let finished = false;
    ws.onopen = () => ws.send(JSON.stringify({ t: "hello", token: s.token, nonce: s.nonce }));
    ws.onmessage = (ev) => {
      if (typeof ev.data !== "string") {
        const url = URL.createObjectURL(ev.data);
        setFrameUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
        return;
      }
      const msg = JSON.parse(ev.data);
      if (msg.t === "status") {
        if (msg.state === "live") setPhase("live");
        if (msg.viewport) setFrameSize(msg.viewport);
      } else if (msg.t === "success") {
        finished = true; setPhase("success"); onSuccess();
      } else if (msg.t === "error") {
        finished = true; setPhase("error"); setMessage(msg.message || "Something went wrong. Retry.");
      } else if (msg.t === "ended") {
        finished = true; setPhase("ended"); setMessage(END_REASONS[msg.reason] || "Session closed.");
      }
    };
    ws.onclose = () => {
      wsRef.current = null;
      if (!finished && phaseRef.current !== "idle") { setPhase("error"); setMessage("Connection lost (server restart or network). Retry."); }
    };
  }, [accountId, onSuccess, onExisting]);

  // Always cancel at account level too: works even if this tab never connected.
  const cancel = useCallback(async () => {
    if (wsRef.current) send({ t: "cancel" });
    await api.post(`/integrations/dpd/accounts/${accountId}/manual-login/cancel`).catch(() => {});
    if (wsRef.current) wsRef.current.close();
    setPhase("idle"); setMessage("");
  }, [accountId, send]);

  useEffect(() => () => { if (wsRef.current) wsRef.current.close(); }, []);
  return { phase, message, frameUrl, expiresAt, frameSize, connect, cancel, send };
}

const RemoteScreen = ({ frameUrl, frameSize, send, live }) => {
  const imgRef = useRef(null);
  const inputRef = useRef(null);
  const touchY = useRef(null);
  const wheel = useRef(0);

  const onClick = (e) => {
    if (!live) return;
    const p = normalizePoint(e.clientX, e.clientY, imgRef.current?.getBoundingClientRect(),
                             frameSize.width, frameSize.height);
    if (p) send({ t: "click", ...p });
    inputRef.current?.focus({ preventScroll: true });
  };
  const onWheel = (e) => {
    if (!live) return;
    wheel.current += e.deltaY;
    if (Math.abs(wheel.current) >= 40) { send({ t: "scroll", dy: Math.round(wheel.current) }); wheel.current = 0; }
  };
  const onTouchMove = (e) => {
    const y = e.touches[0]?.clientY;
    if (!live || touchY.current == null || y == null) return;
    const dy = touchY.current - y;
    if (Math.abs(dy) > 24) { send({ t: "scroll", dy: Math.round(dy * 2) }); touchY.current = y; }
  };
  const onKeyDown = (e) => {
    const msg = keyMessage(e);
    if (msg && live) { e.preventDefault(); send(msg); }
  };
  const onChange = (e) => {
    const msg = diffSentinel(e.target.value);
    if (msg && live) send(msg);
    e.target.value = SENTINEL;
  };

  return (
    <div className="relative mx-auto rounded-xl overflow-hidden border border-brand-border bg-slate-100 select-none"
         style={{ aspectRatio: `${frameSize.width} / ${frameSize.height}`,
                  width: `min(100%, calc(78vh * ${frameSize.width / frameSize.height}))` }}
         data-testid="manual-login-screen" onWheel={onWheel}
         onTouchStart={(e) => { touchY.current = e.touches[0]?.clientY ?? null; }}
         onTouchMove={onTouchMove}>
      {frameUrl ? (
        <img ref={imgRef} src={frameUrl} alt="DPD portal (live)" draggable={false} onClick={onClick}
             className={`w-full h-full object-contain ${live ? "cursor-pointer" : "opacity-60"}`}
             data-testid="manual-login-frame" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-sm text-brand-text-muted">
          {live ? <Loader2 className="w-5 h-5 animate-spin" /> : "Press Connect to open the DPD portal"}
        </div>
      )}
      <input ref={inputRef} defaultValue={SENTINEL} onKeyDown={onKeyDown} onChange={onChange}
             autoCapitalize="off" autoCorrect="off" autoComplete="off" spellCheck={false}
             aria-label="Keyboard input for DPD portal" data-testid="manual-login-keyboard"
             className="absolute bottom-0 left-0 w-px h-px opacity-0" />
    </div>
  );
};

const ExistingSessionPanel = ({ session, onResume, onCancel, busy }) => (
  <Card className="p-4 rounded-2xl border-amber-200 bg-amber-50 flex items-center justify-between gap-3 flex-wrap"
        data-testid="manual-login-existing">
    <div className="text-sm text-amber-900">
      An interactive DPD login is already open for this account
      {session?.mine === false ? " (started by another admin)" : ""}
      {session?.started_at ? `, started ${new Date(session.started_at).toLocaleTimeString()}` : ""}.
      It may be in another tab or a lost connection.
    </div>
    <div className="flex gap-2">
      <Button size="sm" onClick={onResume} disabled={busy} className="bg-brand-red hover:bg-brand-red-hover"
              data-testid="manual-login-resume">
        <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Resume here
      </Button>
      <Button size="sm" variant="outline" onClick={onCancel} disabled={busy} data-testid="manual-login-cancel-existing">
        <X className="w-3.5 h-3.5 mr-1.5" /> Cancel that session
      </Button>
    </div>
  </Card>
);

const SuccessPanel = () => (
  <Card className="p-8 rounded-2xl border-emerald-200 bg-emerald-50 text-center" data-testid="manual-login-success">
    <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600" />
    <div className="mt-3 font-heading text-xl font-bold text-emerald-900">DPD login saved</div>
    <p className="mt-1 text-sm text-emerald-800">The secure browser is closed. Returning to DPD Accounts — you can run an import from there.</p>
  </Card>
);

export default function DpdManualLogin() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { data: account } = useSWR(`/integrations/dpd/accounts/${accountId}`, fetcher);
  const [viewport, setViewport] = useState(defaultViewport);
  const [now, setNow] = useState(Date.now());
  const backTo = useCallback((qs = "") => navigate(`/admin/integrations/dpd${qs}`), [navigate]);
  const onSuccess = useCallback(() => {
    setTimeout(() => backTo(`?manual_login=success&account=${accountId}`), 2500);
  }, [accountId, backTo]);
  const { data: current, mutate: refreshCurrent } = useSWR(
    `/integrations/dpd/accounts/${accountId}/manual-login/current`, fetcher);
  const [busy, setBusy] = useState(false);
  const onExisting = useCallback((flag) => { if (flag) refreshCurrent(); }, [refreshCurrent]);
  const s = useManualLoginSession(accountId, onSuccess, onExisting);
  const active = s.phase === "connecting" || s.phase === "live";
  const existing = !active && s.phase !== "success" ? current?.session : null;
  const cancelExisting = async () => {
    setBusy(true); await s.cancel(); await refreshCurrent(); setBusy(false);
  };
  const resumeHere = async () => {
    setBusy(true); await s.cancel(); await refreshCurrent(); setBusy(false); s.connect(viewport);
  };

  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  const remaining = active && s.expiresAt ? new Date(s.expiresAt).getTime() - now : null;

  const onCancel = async () => { await s.cancel(); backTo(); };

  return (
    <div className="p-3 md:p-8 space-y-3 md:space-y-4 max-w-6xl" data-testid="dpd-manual-login-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button type="button" onClick={() => backTo()} className="text-xs text-brand-text-muted hover:text-brand-text flex items-center gap-1"
                  data-testid="manual-login-back"><ArrowLeft className="w-3.5 h-3.5" /> DPD Accounts</button>
          <h1 className="font-heading text-xl md:text-3xl font-bold tracking-tight text-brand-text mt-1">Interactive DPD login</h1>
          <div className="text-sm text-brand-text-muted" data-testid="manual-login-account-name">{account?.account_name || "…"}</div>
        </div>
        <ConnectionIndicator phase={s.phase} remaining={remaining} />
      </div>
      <SecurityNotice />
      {s.phase === "success" ? <SuccessPanel /> : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <ViewportPicker value={viewport} onChange={setViewport} disabled={active} />
            {!active && !existing && (
              <Button onClick={() => s.connect(viewport)} className="bg-brand-red hover:bg-brand-red-hover"
                      data-testid="manual-login-connect">
                {s.phase === "idle" ? <Plug className="w-4 h-4 mr-1.5" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
                {s.phase === "idle" ? "Connect" : "Retry"}
              </Button>
            )}
            {s.phase === "connecting" && <Loader2 className="w-4 h-4 animate-spin text-brand-text-muted" />}
            <Button variant="outline" onClick={onCancel} data-testid="manual-login-cancel">
              <X className="w-4 h-4 mr-1.5" /> Cancel
            </Button>
          </div>
          {existing && (
            <ExistingSessionPanel session={existing} onResume={resumeHere} onCancel={cancelExisting} busy={busy} />
          )}
          {s.message && !existing && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="manual-login-message">{s.message}</div>
          )}
          <RemoteScreen frameUrl={s.frameUrl} frameSize={s.frameSize} send={s.send} live={s.phase === "live"} />
          <p className="text-xs text-brand-text-muted" data-testid="manual-login-instructions">
            Tap or click a field on the DPD screen, then type. Scroll with the mouse wheel or by swiping. Complete any
            DPD code or CAPTCHA yourself. When DPD shows your account, the login is saved automatically and this page
            returns to DPD Accounts. The session closes after 10 minutes, or 2 minutes without input.
          </p>
        </>
      )}
    </div>
  );
}
